import request from '@api/request';
import { DoctorReview } from '@models/consultant';
import {
  CalendarAvailableTimeSlotsPayloadModel,
  CalendarAvailableTimeSlotsResponseModel,
  CalendarPayloadModel,
  CalendarResponseModel,
  DoctorClinicPayloadModel,
  DoctorClinicResponseModel,
  DoctorReviewSource,
  GetInPersonBookingHistoryParams,
  GetInPersonBookingPayloadModel,
  InPersonBookingCitiesPayloadModel,
  InPersonBookingCitiesResponseModel,
  InPersonBookingHistory,
  InPersonBookingHistoryDetail,
  InPersonBookingHistoryDetailResponse,
  InPersonBookingHistoryPageData,
  InPersonBookingHistoryResponse,
  InPersonBookingReservePayloadModel,
  InPersonBookingUpdatePayloadModel,
} from '@models/in-person-booking';
import { buildQueryParams } from '@utilities/buildQueryParams';

export const getDoctorClinic = async ({
  doctorId,
  pageNumber = 1,
  pageSize = 30,
}: DoctorClinicPayloadModel): Promise<DoctorClinicResponseModel> => {
  const response = await request.get(
    `panel/clinic/get-by-doctor?DoctorId=${doctorId}&PageNumber=${pageNumber}&PageSize=${pageSize}&Filter=1`,
  );
  return response;
};

export const getCalendar = async ({
  doctorId,
  clinicId,
}: CalendarPayloadModel): Promise<CalendarResponseModel> => {
  const response = await request.get(
    `/Calendar/get?DoctorId=${doctorId}&ClinicId=${clinicId}`,
  );
  return response;
};

export const getCalendarAvailableTimeSlots = async ({
  doctorId,
  clinicId,
  date,
}: CalendarAvailableTimeSlotsPayloadModel): Promise<CalendarAvailableTimeSlotsResponseModel> => {
  const response = await request.get(
    `Calendar/get-calendar-available-time-slots?DoctorId=${doctorId}&ClinicId=${clinicId}&Date=${date}`,
  );
  return response;
};

export const getInPersonBookingCities = async ({
  latestSpecialityId,
}: InPersonBookingCitiesPayloadModel = {}): Promise<InPersonBookingCitiesResponseModel> => {
  const query = buildQueryParams({
    ...(latestSpecialityId ? { latestSpecialityId } : {}),
  });
  const response = await request.get(
    `/InPersonBooking/get-cities${query ? `?${query}` : ''}`,
  );
  return response;
};

export const getInPersonBooking = async (
  body: GetInPersonBookingPayloadModel,
): Promise<any> => {
  const query = buildQueryParams(body);
  const response = await request.get(`/InPersonBooking/get?${query}`);
  return response;
};

export const inPersonBookingReserve = async (
  body: InPersonBookingReservePayloadModel,
): Promise<any> => {
  const response = await request.post(`/InPersonBooking/reserve`, body);
  return response;
};

export const inPersonBookingUpdate = async (
  body: InPersonBookingUpdatePayloadModel,
): Promise<any> => {
  const response = await request.put(`/InPersonBooking/update`, body);
  return response;
};

const normalizeDoctorReviewTypeNames = (
  review?: DoctorReview | null,
): DoctorReview | null | undefined => {
  if (!review) return review;

  const toStringArray = (value: unknown): string[] | null | undefined => {
    if ([undefined, null].includes(value)) {
      return value as null | undefined;
    }
    if (Array.isArray(value)) {
      return value.filter((v): v is string => typeof v === 'string');
    }
    if (typeof value === 'string') {
      return [value];
    }
    return undefined;
  };

  const normalized: DoctorReview = {
    ...review,
    satisfactionTypeName: toStringArray(review.satisfactionTypeName),
    dissatisfactionTypeName: toStringArray(review.dissatisfactionTypeName),
  };

  return normalized;
};

const resolveDoctorReview = (item: DoctorReviewSource) =>
  normalizeDoctorReviewTypeNames(
    item.doctorReview ?? item.doctorReviews?.[0] ?? null,
  ) ?? null;

const normalizeInPersonBookingHistoryItem = (
  item: InPersonBookingHistory,
): InPersonBookingHistory => {
  const resolved = resolveDoctorReview(item);
  const hasReview =
    typeof item.hasReview === 'boolean'
      ? item.hasReview
      : Boolean(resolved?.id);

  return {
    ...item,
    id: item.id ?? item.recId,
    hasReview,
    doctorReview: resolved,
  };
};

const normalizeInPersonBookingHistoryPage = (
  response: InPersonBookingHistoryResponse | InPersonBookingHistoryPageData,
): InPersonBookingHistoryResponse => {
  const page =
    'data' in response && response.data
      ? response
      : {
          isSuccess: true,
          message: '',
          resultCode: '',
          errors: null,
          data: response as InPersonBookingHistoryPageData,
        };

  return {
    ...page,
    data: {
      ...page.data,
      queryResult: (page.data.queryResult ?? []).map(
        normalizeInPersonBookingHistoryItem,
      ),
    },
  };
};

export const getInPersonBookingHistoryPageData = (
  response: InPersonBookingHistoryResponse | InPersonBookingHistoryPageData,
) => normalizeInPersonBookingHistoryPage(response).data;

export const getInPersonBookingHistory = (
  params?: GetInPersonBookingHistoryParams,
) => {
  const query = buildQueryParams({
    status: params?.status,
    pageNumber: params?.pageNumber,
    pageSize: params?.pageSize,
  });

  return request
    .get<
      InPersonBookingHistoryResponse | InPersonBookingHistoryPageData
    >(`/InPersonBooking/get-in-person-booking-history?${query}`)
    .then(normalizeInPersonBookingHistoryPage);
};

const normalizeInPersonBookingHistoryDetail = (
  response: InPersonBookingHistoryDetailResponse | InPersonBookingHistoryDetail,
): InPersonBookingHistoryDetailResponse => {
  const wrapped: InPersonBookingHistoryDetailResponse =
    'data' in response && response.data
      ? response
      : {
          isSuccess: true,
          message: '',
          resultCode: '',
          errors: null,
          data: response as InPersonBookingHistoryDetail,
        };

  const detail = wrapped.data;

  return {
    ...wrapped,
    data: {
      ...detail,
      doctorReview: !detail.hasReview ? null : resolveDoctorReview(detail),
    },
  };
};

export const getInPersonBookingHistoryDetailByRecId = (recId: number) => {
  return request
    .get<
      InPersonBookingHistoryDetailResponse | InPersonBookingHistoryDetail
    >(`/InPersonBooking/get-in-person-booking-detail?RecId=${recId}`)
    .then(normalizeInPersonBookingHistoryDetail);
};
